/**
 * Customer referrals API.
 *
 * Endpoints (verified against /openapi.json — tag "Customer Referrals"):
 *   GET /api/v1/customer/referrals/code    → the caller's referral code
 *                                            (generated on first request)
 *   GET /api/v1/customer/referrals/status  → people the caller has referred,
 *                                            with masked mobile + reward points
 *
 * Read-only — there is no mutation here. A new account "uses" a referral code
 * at signup time via CustomerSignupVerifyRequest.referral_code (see api/auth.ts).
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { ApiError, createKeyFactory, type Schemas } from '@finmin/api-client';
import { apiClient } from '@/lib/api';

export type ReferralCodeResponse       = Schemas['ReferralCodeResponse'];
export type ReferralStatusItem         = Schemas['ReferralStatusItem'];
export type ReferralStatusListResponse = Schemas['ReferralStatusListResponse'];

export const referralKeys = createKeyFactory('customer.referrals', {
  code:   () => ['code'] as const,
  status: () => ['status'] as const,
});

export async function getReferralCode(signal?: AbortSignal) {
  const { data } = await apiClient.get<ReferralCodeResponse>(
    '/api/v1/customer/referrals/code',
    { signal },
  );
  return data;
}

export async function getReferralStatus(signal?: AbortSignal) {
  const { data } = await apiClient.get<ReferralStatusListResponse>(
    '/api/v1/customer/referrals/status',
    { signal },
  );
  return data;
}

/** The referral code is stable once minted, so it can stay fresh indefinitely. */
export function useReferralCode(
  options?: Omit<UseQueryOptions<ReferralCodeResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ReferralCodeResponse, ApiError>({
    queryKey: referralKeys.code(),
    queryFn: ({ signal }) => getReferralCode(signal),
    staleTime: Infinity,
    ...options,
  });
}

export function useReferralStatus(
  options?: Omit<UseQueryOptions<ReferralStatusListResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ReferralStatusListResponse, ApiError>({
    queryKey: referralKeys.status(),
    queryFn: ({ signal }) => getReferralStatus(signal),
    ...options,
  });
}
